import React from "react";
import MultiSelect from "@/modules/shared/ui/forms/MultiSelect";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import {
    SET_SELECTED_ORDER_EXTERNAL_IDS,
    RESET_FILTERS,
} from "@/modules/pg/shared/planningTable/reducers/filters/filters";
import { connect } from "react-redux";
import type { Dispatch } from "redux";

import type {
    SetSelectedOrderUuids,
    ResetFiltersAction,
} from "@/modules/pg/shared/planningTable/reducers/filters/filters";
import type { ReduxState } from "@/modules/pg/shared/planningTable/reducers/reducers";

export type SelectOptionItem = {
    label: string;
    value: string;
};

type ActionTypes = SetSelectedOrderUuids | ResetFiltersAction;
type OwnProps = {};
type Props = OwnProps & ReduxProps;

const replaceSpaces = (text: string = "") => {
    return text
        .split(" ")
        .filter(v => v.trim())
        .join(" ");
};

const filterOption = (candidate: { label: string; value: string; data: unknown }, input: string) => {
    if (input) {
        return (
            replaceSpaces(candidate.label).toLowerCase().includes(replaceSpaces(input).toLowerCase()) ||
            candidate.value === (input || "").trim()
        );
    }
    return true;
};

const groupStyles: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
};

const groupBadgeStyles: React.CSSProperties = {
    backgroundColor: "#EBECF0",
    borderRadius: "2em",
    color: "#172B4D",
    display: "inline-block",
    fontSize: 12,
    fontWeight: "normal",
    lineHeight: "1",
    minWidth: 1,
    padding: "0.16666666666667em 0.5em",
    textAlign: "center",
};

const formatGroupLabel = (data: GroupedOption) => (
    <div style={groupStyles}>
        <span>{data.label}</span>
        <span style={groupBadgeStyles}>{data.options.length}</span>
    </div>
);

type GroupedOption = {
    label: string;
    options: SelectOptionItem[];
};

// TODO - when reschedule order or line is enabled then we need to reset the filter
class OrderFilter extends React.Component<Props> {
    mapLineOrdersToOptions = (): SelectOptionItem[] => {
        const unscheduled_orders = this.props.unscheduled_orders;
        const lines_orders = this.props.lines_orders;
        if (!lines_orders && !unscheduled_orders) {
            return [];
        }

        const order_external_id_set: Set<string> = new Set();
        for (const line_order of [...lines_orders, ...unscheduled_orders]) {
            for (const order of line_order.orders) {
                order_external_id_set.add(order.external_id);
            }
        }

        const options: SelectOptionItem[] = [];
        for (const order_external_id of order_external_id_set) {
            options.push({
                value: order_external_id,
                label: order_external_id,
            });
        }
        options.sort((a, b) => a.value.localeCompare(b.value));

        return options;
    };

    getOptions = (): SelectOptionItem[] => {
        return this.mapLineOrdersToOptions();
    };

    getSelectedOptions = (order_uuids: string[] | null): SelectOptionItem[] => {
        if (!order_uuids) {
            return [];
        }

        const options = this.getOptions();
        const selected_options: SelectOptionItem[] = [];

        for (const option of options) {
            if (order_uuids.includes(option.value)) {
                selected_options.push(option);
            }
        }

        return selected_options;
    };

    onChange = (options?: SelectOptionItem[]) => {
        if (!options) {
            this.props.reduxDispatch({ type: RESET_FILTERS, data: undefined });
        } else {
            this.props.reduxDispatch({ type: SET_SELECTED_ORDER_EXTERNAL_IDS, data: options.map(o => o.value) });
        }
    };

    render() {
        return (
            <div className="order-filter">
                <MultiSelect
                    value={this.getSelectedOptions(this.props.selected_order_external_ids)}
                    placeholder={translate("Header.menu.workorders", "Work-orders")}
                    options={this.getOptions()}
                    filterOption={filterOption}
                    formatGroupLabel={formatGroupLabel}
                    onChange={this.onChange}
                    enable_filter_selected
                    menuPortalTarget={document.body}
                    styles={{
                        menuPortal: base => ({ ...base, zIndex: 100 }),
                        menu: base => ({ ...base, width: "max-content", minWidth: "100%" }),
                    }}
                />
            </div>
        );
    }
}

const mapState = (state: ReduxState) => {
    const selected_order_external_ids = state.gantt_chart_filters.selected_order_external_ids;
    const lines_orders = state.gantt_chart_lines_orders.lines_orders;
    const unscheduled_orders = state.gantt_chart_lines_orders.unscheduled_orders;

    return {
        selected_order_external_ids,
        lines_orders,
        unscheduled_orders,
    };
};
const mapDispatch = (dispatch: Dispatch<ActionTypes>) => ({
    reduxDispatch: dispatch,
});

type ReduxProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;
export default connect<ReturnType<typeof mapState>, ReturnType<typeof mapDispatch>, OwnProps, ReduxState>(
    mapState,
    mapDispatch
)(OrderFilter);
